import React from 'react';
import { FormField } from '../../../components/FormField';
import { FormRow } from '../../../components/FormRow';
import { views } from '../../../constants/propertyOptions';
import { formatArea } from '../../../../../../utils/formatters';

interface CommercialOptionsProps {
  formData: any;
  handleInputChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
}

const commercialTypes = [
  'Boutique',
  'Magasin',
  'Bureau',
  'Entrepôt',
  'Atelier',
  'Showroom',
  'Espace de coworking',
  'Restaurant',
  'Hôtel',
  'Centre commercial',
  'Centre médical',
  'Local de Service',
];

const environments = [
  'Zone commerciale',
  'Centre-ville',
  'Périphérie',
  'Zone industrielle',
  'Résidentielle',
];

const conditions = [
  'Neuf',
  'Rénové',
  'Ancien',
  'À rénover',
];

const accessibility = [
  'Voies d\'accès principales',
  'Visibilité depuis la rue',
  'Isolé',
];

const customerFlow = [
  'Zone à fort passage piéton',
  'Zone à fort passage véhicules',
  'Fréquentation horaire',
];

export const CommercialOptions: React.FC<CommercialOptionsProps> = ({
  formData,
  handleInputChange,
}) => {
  // Gestionnaire pour les champs de surface (supprime les décimales)
  const handleAreaChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    // Supprimer tout sauf les chiffres
    const numericValue = value.replace(/[^\d]/g, '');
    
    handleInputChange({
      target: {
        name,
        value: numericValue,
        type: 'text'
      }
    } as React.ChangeEvent<HTMLInputElement>);
  };
  return (
    <div className="space-y-6">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white">
        Caractéristiques du local commercial
      </h3>

      {/* Informations générales */}
      <div className="space-y-4">
        <FormRow>
          <FormField label="Type de local">
            <select
              name="commercialType"
              value={formData.commercialType || ''}
              onChange={handleInputChange}
              className="w-full"
            >
              <option value="">Sélectionner</option>
              {commercialTypes.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </FormField>

          <FormField label="État général">
            <select
              name="condition"
              value={formData.condition || ''}
              onChange={handleInputChange}
              className="w-full"
            >
              <option value="">Sélectionner</option>
              {conditions.map(condition => (
                <option key={condition} value={condition}>{condition}</option>
              ))}
            </select>
          </FormField>
        </FormRow>

        <FormRow>
          <FormField label="Superficie totale (m²)">
            <input
              type="text"
              name="totalArea"
              value={formatArea(formData.totalArea)}
              onChange={handleAreaChange}
              placeholder="0"
            />
          </FormField>

          <FormField label="Année de construction">
            <input
              type="number"
              name="constructionYear"
              value={formData.constructionYear || ''}
              onChange={handleInputChange}
              min="1800"
              max={new Date().getFullYear()}
            />
          </FormField>
        </FormRow>

        <FormField label="Disponibilité">
          <input
            type="text"
            name="availability"
            value={formData.availability || ''}
            onChange={handleInputChange}
          />
        </FormField>
      </div>

      {/* Localisation et accessibilité */}
      <div className="space-y-4">
        <h4 className="font-medium text-gray-700 dark:text-gray-300">
          Localisation et accessibilité
        </h4>

        <FormRow>
          <FormField label="Environnement immédiat">
            <select
              name="environment"
              value={formData.environment || ''}
              onChange={handleInputChange}
              className="w-full"
            >
              <option value="">Sélectionner</option>
              {environments.map(env => (
                <option key={env} value={env}>{env}</option>
              ))}
            </select>
          </FormField>

          <FormField label="Accessibilité">
            <select
              name="accessibility"
              value={formData.accessibility || ''}
              onChange={handleInputChange}
              className="w-full"
            >
              <option value="">Sélectionner</option>
              {accessibility.map(acc => (
                <option key={acc} value={acc}>{acc}</option>
              ))}
            </select>
          </FormField>
        </FormRow>

        <FormField label="Flux de clientèle">
          <div className="space-y-2">
            {customerFlow.map(flow => (
              <label key={flow} className="flex items-center gap-2">
                <input
                  type="checkbox"
                  name={`customerFlow_${flow}`}
                  checked={formData[`customerFlow_${flow}`] || false}
                  onChange={handleInputChange}
                />
                <span>{flow}</span>
              </label>
            ))}
          </div>
        </FormField>
      </div>

      {/* Configuration du local */}
      <div className="space-y-4">
        <h4 className="font-medium text-gray-700 dark:text-gray-300">
          Configuration du local
        </h4>

        <FormField label="Surface détaillée (m²)">
          <div className="grid grid-cols-2 gap-4">
            <input
              type="text"
              name="salesArea"
              value={formatArea(formData.salesArea)}
              onChange={handleAreaChange}
              placeholder="Surface de vente"
            />
            <input
              type="text"
              name="backroomArea"
              value={formatArea(formData.backroomArea)}
              onChange={handleAreaChange}
              placeholder="Arrière-boutique"
            />
            <input
              type="text"
              name="storageArea"
              value={formatArea(formData.storageArea)}
              onChange={handleAreaChange}
              placeholder="Réserve"
            />
            <input
              type="text"
              name="officeArea"
              value={formatArea(formData.officeArea)}
              onChange={handleAreaChange}
              placeholder="Bureaux"
            />
            <input
              type="text"
              name="mezzanineArea"
              value={formatArea(formData.mezzanineArea)}
              onChange={handleAreaChange}
              placeholder="Mezzanine"
            />
            <input
              type="text"
              name="warehouseArea"
              value={formatArea(formData.warehouseArea)}
              onChange={handleAreaChange}
              placeholder="Entrepôt"
            />
          </div>
        </FormField>

        <FormField label="Configuration du local">
          <div className="grid grid-cols-2 gap-4">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                name="isOpenSpace"
                checked={formData.isOpenSpace || false}
                onChange={handleInputChange}
              />
              <span>Open space</span>
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                name="hasPartitions"
                checked={formData.hasPartitions || false}
                onChange={handleInputChange}
              />
              <span>Cloisonné</span>
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                name="hasMezzanine"
                checked={formData.hasMezzanine || false}
                onChange={handleInputChange}
              />
              <span>Avec mezzanine</span>
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                name="hasAdditionalFloors"
                checked={formData.hasAdditionalFloors || false}
                onChange={handleInputChange}
              />
              <span>Étage(s) supplémentaires</span>
            </label>
          </div>
        </FormField>

        <FormRow>
          <FormField label="Façade">
            <div className="space-y-2">
              <input
                type="text"
                name="facadeLength"
                value={formatArea(formData.facadeLength)}
                onChange={handleAreaChange}
                placeholder="Longueur (m)"
              />
              <div className="flex gap-4">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    name="hasGlassFacade"
                    checked={formData.hasGlassFacade || false}
                    onChange={handleInputChange}
                  />
                  <span>Façade vitrée</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    name="hasAutomaticDoor"
                    checked={formData.hasAutomaticDoor || false}
                    onChange={handleInputChange}
                  />
                  <span>Porte automatique</span>
                </label>
              </div>
            </div>
          </FormField>

          <FormField label="Nombre d'entrées/sorties">
            <div className="space-y-2">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  name="hasMainEntrance"
                  checked={formData.hasMainEntrance || false}
                  onChange={handleInputChange}
                />
                <span>Porte principale</span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  name="hasSecondaryEntrance"
                  checked={formData.hasSecondaryEntrance || false}
                  onChange={handleInputChange}
                />
                <span>Porte secondaire</span>
              </label>
            </div>
          </FormField>
        </FormRow>
      </div>

      {/* Équipements et sécurité */}
      <div className="space-y-4">
        <h4 className="font-medium text-gray-700 dark:text-gray-300">
          Équipements et sécurité
        </h4>

        <FormField label="Équipements de base">
          <div className="grid grid-cols-2 gap-4">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                name="hasSanitaries"
                checked={formData.hasSanitaries || false}
                onChange={handleInputChange}
              />
              <span>Sanitaires</span>
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                name="hasSink"
                checked={formData.hasSink || false}
                onChange={handleInputChange}
              />
              <span>Lavabo</span>
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                name="hasKitchenette"
                checked={formData.hasKitchenette || false}
                onChange={handleInputChange}
              />
              <span>Kitchenette</span>
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                name="hasBreakRoom"
                checked={formData.hasBreakRoom || false}
                onChange={handleInputChange}
              />
              <span>Espace de pause</span>
            </label>
          </div>
        </FormField>

        <FormField label="Sécurité">
          <div className="grid grid-cols-2 gap-4">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                name="hasAlarm"
                checked={formData.hasAlarm || false}
                onChange={handleInputChange}
              />
              <span>Alarme</span>
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                name="hasVideoSurveillance"
                checked={formData.hasVideoSurveillance || false}
                onChange={handleInputChange}
              />
              <span>Vidéosurveillance</span>
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                name="hasFireExtinguishers"
                checked={formData.hasFireExtinguishers || false}
                onChange={handleInputChange}
              />
              <span>Extincteurs</span>
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                name="hasMetalCurtains"
                checked={formData.hasMetalCurtains || false}
                onChange={handleInputChange}
              />
              <span>Rideaux métalliques</span>
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                name="hasSecurityLocks"
                checked={formData.hasSecurityLocks || false}
                onChange={handleInputChange}
              />
              <span>Serrures de sécurité</span>
            </label>
          </div>
        </FormField>

        <FormField label="Vue">
          <select
            name="view"
            value={formData.view || ''}
            onChange={handleInputChange}
            className="w-full"
          >
            <option value="">Sélectionner</option>
            {views.map(view => (
              <option key={view} value={view}>{view}</option>
            ))}
          </select>
        </FormField>

        <FormField label="Espaces extérieurs">
          <div className="grid grid-cols-2 gap-4">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                name="hasTerrace"
                checked={formData.hasTerrace || false}
                onChange={handleInputChange}
              />
              <span>Terrasse</span>
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                name="hasGarden"
                checked={formData.hasGarden || false}
                onChange={handleInputChange}
              />
              <span>Jardin</span>
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                name="hasCourt"
                checked={formData.hasCourt || false}
                onChange={handleInputChange}
              />
              <span>Cour</span>
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                name="hasPrivateParking"
                checked={formData.hasPrivateParking || false}
                onChange={handleInputChange}
              />
              <span>Parking privatif</span>
            </label>
          </div>
        </FormField>

      </div>
    </div>
  );
};